import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sun, Moon, ArrowLeft, Loader2 } from 'lucide-react';
import SenArtisteLogo from '../components/SenArtisteLogo';
import LanguageSelector from '../components/LanguageSelector';

const Login = () => {
  const { artistSlug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password, artistSlug);
      toast.success('Connexion réussie!');
      
      // Vérifier si changement de mot de passe obligatoire
      if (user.mustChangePassword) {
        toast.info('Veuillez changer votre mot de passe');
        navigate('/changer-mot-de-passe');
        return;
      }
      
      // Redirect based on role
      if (user.role === 'SUPER_ADMIN') {
        navigate('/admin');
      } else if (user.role === 'APPORTEUR') {
        navigate('/apporteur/dashboard');
      } else if (user.role === 'SCANNER') {
        // SCANNER - redirige directement vers le scanner
        const targetSlug = user.artistSlug;
        if (targetSlug) {
          navigate(`/${targetSlug}/scanner`);
        } else {
          toast.error('Aucun artiste associé à ce compte');
          navigate('/');
        }
      } else if (user.role === 'ARTISTE' || user.role === 'MANAGER') {
        // ARTISTE ou MANAGER - redirige vers SON artiste (pas celui de l'URL)
        const targetSlug = user.artistSlug;
        if (targetSlug) {
          navigate(`/${targetSlug}/management`);
        } else {
          toast.error('Aucun artiste associé à ce compte');
          navigate('/');
        }
      } else if (user.role === 'FAN') {
        // Fan - logique de redirection intelligente
        // Si l'URL contient un artistSlug, aller vers cette page artiste
        // Sinon, si le fan a visité plusieurs artistes, aller à la landing
        // Sinon, aller vers la page de l'artiste qu'il a visité
        if (artistSlug) {
          navigate(`/${artistSlug}`);
        } else if (user.visitedArtists && user.visitedArtists.length > 1) {
          // Fan a visité plusieurs artistes -> landing page
          navigate('/');
        } else if (user.artistSlug) {
          // Fan associé à un seul artiste -> page de cet artiste
          navigate(`/${user.artistSlug}`);
        } else {
          navigate('/');
        }
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || 'Identifiants incorrects';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 hero-gradient">
      {/* Background */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(/images/stage-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50"
        data-testid="theme-toggle"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </Button>
      
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate(artistSlug ? `/${artistSlug}` : '/')}
        className="fixed top-4 left-4 z-50"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('back')}
      </Button>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="glass-card">
          <CardHeader className="text-center space-y-2">
            <Link to="/" className="flex items-center justify-center gap-3 mb-4">
              <SenArtisteLogo className="w-16 h-16" />
              <span className="font-syne font-bold text-2xl">Sen Artiste</span>
            </Link>
            <div className="flex justify-center">
              <LanguageSelector />
            </div>
            <CardTitle className="font-syne text-2xl">{t('login')}</CardTitle>
            <CardDescription>
              {artistSlug ? (t('language') === 'fr' ? 'Connectez-vous à la fanpage' : 'Login to the fanpage') : (t('language') === 'fr' ? 'Accédez à votre compte' : 'Access your account')}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password">{t('password')}</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-primary hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  t('signIn')
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {t('noAccount')}{' '}
                <Link 
                  to={artistSlug ? `/${artistSlug}/register` : '/register'} 
                  className="text-primary hover:underline"
                  data-testid="register-link"
                >
                  {t('signUp')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Login;
